import { getOrgEntities } from "@/backend/http/user";
import { getArtsAndTeams } from "@/backend/read/BackendReader";
import { captureException } from "@/error/sentry";
import type { BoardType, Id } from "@/model/baseTypes";
import type { Session } from "@/model/session";
import type { TechnicalUser, UserOrgEntities } from "@/model/user";
import { useArtStore } from "@/store/art";
import { useSessionStore } from "@/store/session";
import { useTeamStore } from "@/store/team";
import { isExecuting, isPlanning } from "@/utils/session";

// - There's exactly one session the user is assigned to (via art or team) in planning -> return it
// - There's exactly one session the user is assigned to (via art or team) in execution -> return it
export async function uniqueActiveSession(user: TechnicalUser): Promise<Session | undefined> {
  const userOrgEntities = await getOrgEntities(user.id);
  const sessions = useSessionStore().sessions;
  return (
    (await uniqueAssignedSession(
      sessions.filter((session) => isPlanning(session)),
      userOrgEntities,
    )) ||
    (await uniqueAssignedSession(
      sessions.filter((session) => isExecuting(session)),
      userOrgEntities,
    ))
  );
}

async function uniqueAssignedSession(
  sessions: Session[],
  entities: UserOrgEntities,
): Promise<Session | undefined> {
  const sessionsArtsTeams = await Promise.all(
    sessions.map((session) =>
      getArtsAndTeams(session.id).then((artsAndTeams) => ({ session, ...artsAndTeams })),
    ),
  );
  const artAssigned = sessionsArtsTeams.filter(({ arts }) => choose(arts, entities.art).length > 0);
  if (artAssigned.length === 1) {
    return artAssigned[0].session;
  }
  const teamAssigned = sessionsArtsTeams.filter(
    ({ teams }) => choose(teams, entities.team).length > 0,
  );
  if (teamAssigned.length === 1) {
    return teamAssigned[0].session;
  }
}

interface TeamAndBoard {
  team: string;
  board: BoardType;
}

// - observer/user:
//     - the user is assigned to a team -> go to the team board of this team
//     - else -> go to program board, select first team of first ART
// - pi-admin/admin:
//     - the user is assigned to an ART -> go to program board of this ART, select an assigned team
//     - the user is assigned to a team -> go where observer/user would go
//     - else -> go to the program board, select first team of first ART
export async function initialTeamAndBoard(user: TechnicalUser): Promise<TeamAndBoard> {
  try {
    return await appParams(user);
  } catch (fail) {
    captureException(fail);
    const team = useTeamStore().teams[0];
    return { board: "team", team: team.name };
  }
}

async function appParams(user: TechnicalUser): Promise<TeamAndBoard> {
  switch (user.role) {
    case "observer":
    case "user":
      return handleUser(user);
    case "admin":
    case "planning_interval_admin":
      return await handleAdmin(user);
    default:
      throw Error("Unhandled role", user.role);
  }
}

function handleUser(user: TechnicalUser): TeamAndBoard {
  const teams = choose(useTeamStore().teams, user.teams);
  if (teams.length > 0) {
    return { team: teams[0].name, board: "team" };
  }
  // the user might belong to no team in the session, meaning he's from a participating team
  // in this case, select the first team of the first art
  const firstArtId = useArtStore().arts[0]?.id || "";
  const teamsInFirstArt = useTeamStore().teamsInArt(firstArtId);
  return { team: teamsInFirstArt[0].name, board: "program" };
}

async function handleAdmin(user: TechnicalUser): Promise<TeamAndBoard> {
  const userOrgEntities = await getOrgEntities(user.id);

  const arts = choose(useArtStore().arts, userOrgEntities.art);
  const teams = choose(useTeamStore().teams, userOrgEntities.team);

  if (arts.length > 0) {
    const art = arts[0];
    const team = teams.find((t) => t.artId === art.id) ?? useTeamStore().teamsInArt(art.id)[0];
    return { board: "program", team: team.name };
  }

  if (teams.length > 0) {
    return handleUser(user);
  }

  const firstArt = useArtStore().arts[0];
  const team = useTeamStore().teamsInArt(firstArt.id)[0];
  return { board: "program", team: team.name };
}

function choose<T extends Id, S extends Id>(all: T[], selected: S[]): T[] {
  const ids = new Set(selected.map((e) => e.id));
  return all.filter((entity) => ids.has(entity.id));
}
